import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  NumberFormatField,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { PeriodLimitFragment } from '@/fragments/__generated__/Limits';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  ChangePlayerSessionLimitMutation,
  ChangePlayerSessionLimitMutationVariables,
} from './__generated__/PlayerSessionLimitsForm';

const query = graphql`
  query SanityPlayerSessionLimitsForm {
    sanityPlayerSessionLimitsForm {
      title {
        ...SanityLocaleString
      }
      dailyLabel {
        ...SanityLocaleString
      }
      weeklyLabel {
        ...SanityLocaleString
      }
      monthlyLabel {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const changeSessionLimitMutation = gql`
  mutation ChangePlayerSessionLimit(
    $playerId: ID!
    $amount: PositiveBigDecimal!
    $limitPeriodType: LimitTypeEnum!
  ) {
    setSessionLimit(
      amount: $amount
      playerId: $playerId
      limitPeriodType: $limitPeriodType
    ) {
      id
      sessionLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerSessionLimitsForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
  limit: Nullable<PeriodLimitFragment>;
}> = ({ playerId, limit, limitPeriodType }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerSessionLimitsFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerSessionLimitsForm;

  const [changePlayerSessionLimitState, changePlayerSessionLimit] = useMutation<
    ChangePlayerSessionLimitMutation,
    ChangePlayerSessionLimitMutationVariables
  >(changeSessionLimitMutation);

  const defaultValues = {
    amount: limit?.value,
  };

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (values: typeof defaultValues) => {
    if (playerId) {
      changePlayerSessionLimit({
        playerId,
        amount: values.amount ?? 0,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
        } else if (close) {
          close();
        }
      });
    }
  };

  if (!form || !defaultValues) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <NumberFormatField
            title={t(
              limitPeriodType === LimitTypeEnum.Day
                ? form.dailyLabel
                : limitPeriodType === LimitTypeEnum.Week
                ? form.weeklyLabel
                : form.monthlyLabel,
            )}
            name="amount"
            id="PlayerSessionLimitsForm__amount"
            className="col-span-full"
          />

          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={changePlayerSessionLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerSessionLimitsForm;
